<script setup lang="ts">import { ref as _ref, toRef as _toRef, computed as _computed } from 'vue';


import {useWindowSize} from '@vueuse/core'
import {nextTick, onMounted} from 'vue'
import {useKeyboardShortcuts} from '@toolify/client/src/modules/keyboard/composables/useKeyboardShortcuts'
import {KeyboardSpecialKeyTypes} from '@toolify/client/src/stores/KeyboardShortcutStore/enum/KeyboardSpecialKeyTypes'
import {useOverlay} from '@toolify/client/src/modules/modals/composables/useOverlay'

interface Props {
  noBackground?: boolean
  openOnMount?: boolean
  noPadding?: boolean
  maxWidth?: number | string
  background?: 'surface' | 'container-lowest'
}
const props = withDefaults(defineProps<Props>(), {
  noBackground: false,
  openOnMount: false,
  noPadding: false,
  maxWidth: undefined,
  background: 'surface',
})

interface Emits {
  (e: 'exit'): void
}
const emit = defineEmits<Emits>()

let isOpen = _ref(false)
const containerRef = _ref<HTMLElement>(null)
const modalRef = _ref<HTMLElement>(null)
const overlayRef = _ref<HTMLElement>(null)

const __$temp_1 = (useWindowSize()),
  width = _toRef(__$temp_1, 'width'),
  height = _toRef(__$temp_1, 'height');
const maxHeight = _computed(() => {
  return height.value - 64
})

const __$temp_2 = (useOverlay({
  element: (containerRef),
  rootContainer: (modalRef),
  coverElement: (overlayRef),
  isOpen: (isOpen),
  onClose: () => setIsOpen(false),
})),
  zIndex = _toRef(__$temp_2, 'zIndex');

defineExpose({
  close: () => {
    isOpen.value = false
    emit('exit')
  },
})

onMounted(() => {
  if(props.openOnMount) {
    nextTick(() => {
      isOpen.value = true
    })
  }
})

useKeyboardShortcuts([
  [[KeyboardSpecialKeyTypes.Escape], () => {
    setIsOpen(false)
  }],
])

function setIsOpen(value: boolean) {
  isOpen.value = value
  if(!value) {
    emit('exit')
  }
}

const modalStyle = _computed(() => {
  if(!zIndex.value) {
    return {}
  }
  return {
    zIndex: zIndex.value,
  }
})

const containerStyle = _computed(() => {
  if(!props.maxWidth) {
    return {}
  }
  const maxWidth = typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth
  return {
    maxWidth,
    width: '100%',
  }
})

const containerWrapperStyle = _computed(() => {
  if(!props.maxWidth) {
    return {}
  }
  return {
    height: 'auto',
    width: 'auto',
    alignSelf: 'center',
    justifySelf: 'center',
  }
})
</script>

<template>
  <div @click="isOpen = true" v-if="$slots.default">
    <slot/>
  </div>
  <Teleport to="#teleported">
    <div class="modal" ref="modalRef" :style="modalStyle">
      <Transition name="overlay-transition" appear>
        <div
          class="overlay"
          v-if="isOpen"
          ref="overlayRef"
        />
      </Transition>
      <Transition name="container-transition" appear>
        <div
          class="container-wrapper"
          v-if="isOpen"
          :class="{'has-padding': !noPadding}"
          :style="containerWrapperStyle"
        >
          <div
            class="container"
            ref="containerRef"
            :class="{
              'no-background': props.noBackground,
              'is-container-lowest': props.background === 'container-lowest',
            }"
            :style="containerStyle"
          >
            <slot name="content" :maxHeight="maxHeight"/>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
  @use '../../../styles/variables' as variables;

  .modal {
    width: 100%;
    height: 100%;
    display: flex;
    position: absolute;
    left: 0;
    top: 0;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    min-height: 0;
  }

  .overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background: oklch(16% 0.01 245 / 0.85);
    pointer-events: all;
    z-index: 1;
  }

  .container-wrapper {
    box-sizing: border-box;
    max-width: 100%;
    max-height: 100%;
    z-index: 10;
    min-height: 0;
    height: 100%;
    width: 100%;
    display: grid;

    &.has-padding {
      padding: map-get(variables.$spacing, space-8);
    }
  }

  .container {
    max-width: 100%;
    max-height: 100%;
    background: map-get(variables.$colors, surface);
    border-radius: map-get(variables.$radiuses, radius-lg);
    box-sizing: border-box;
    display: grid;
    //grid-template-rows: auto 1fr;
    gap: map-get(variables.$spacing, space-5);
    overflow: hidden;
    pointer-events: all;
    min-height: 0;

    &.no-background {
      background: transparent;
    }

    &.is-container-lowest {
      background: map-get(variables.$colors, container-lowest);
    }

  }

  .container-transition {
    &-enter-active {
      transition: all 0.25s cubic-bezier(0, 0.5, 0, 1.5);
    }
    &-leave-active {
      transition: all 0.25s cubic-bezier(0, 0.5, 0, 1.5);
    }
    &-enter-active,
    &-leave-active {
      transform: none;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateY(5%) scale(0.85);
    }
  }

  .overlay-transition {
    &-enter-active {
      transition: all 0.25s cubic-bezier(0, 0.5, 0, 1.5);
    }
    &-leave-active {
      transition: all 0.25s cubic-bezier(0, 0.5, 0, 1.5);
    }
    &-enter-active,
    &-leave-active {
      transform: none;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
      visibility: hidden;
    }
  }
</style>
